// Sample day demo scripts. All content is simulation with sample data.
// Scripts were reviewed against the locked copy rules before build:
// disclosure at the start of every conversation and call, no price figures
// before the owner approval tap, consent framed calls, clinic boundary.

export type ChatMsg = { from: "user" | "ai"; text: string; time?: string };
export type CallLine = { speaker: "ai" | "cust"; text: string };

export type QueueItem = { name: string; note: string };
export type TeamLine = { who: string; text: string };

export type Vertical = {
  id: string;
  label: string;
  biz: string;
  s1: ChatMsg[];
  s1Caption: string;
  call: CallLine[];
  callLead: string;
  approval: { quote: string; sample: string; approveReply: string; holdReply: string };
  followup: { card: string };
  queue: QueueItem[];
  revival: ChatMsg[];
  team: TeamLine[];
  posts: [string, string];
  reportExtra: string[];
};

export const VERTICALS: Vertical[] = [
  {
    id: "clinic",
    label: "Clinic",
    biz: "Sharma Clinic",
    s1: [
      { from: "user", text: "Hello, is Dr Sharma available tomorrow?", time: "10:41 pm" },
      { from: "ai", text: "Hello! I am the AI assistant at Sharma Clinic. Dr Sharma has slots at 10:30 am and 3:00 pm tomorrow. Which suits you?", time: "10:41 pm" },
      { from: "user", text: "10:30. Also my knee pain is worse, what should I do till then?", time: "10:43 pm" },
      { from: "ai", text: "I can request the 10:30 am slot for you. May I have your name? On the pain, I am an AI assistant and cannot give medical advice or judge urgency. The doctor will guide you at the visit. If you feel it is urgent, please call the clinic directly or local emergency services.", time: "10:43 pm" },
      { from: "user", text: "Ravi Kumar. Ok thank you.", time: "10:44 pm" },
      { from: "ai", text: "Thank you Ravi, your 10:30 am appointment is confirmed. I will send a reminder in the morning.", time: "10:44 pm" },
      { from: "user", text: "Consultation fee kitna hai? Any discount for senior citizens?", time: "10:45 pm" },
      { from: "ai", text: "One moment, I have sent your question to the doctor and will come back the moment he confirms.", time: "10:45 pm" },
    ],
    s1Caption: "In clinics the AI assistant sticks to bookings and reminders. It never gives medical advice.",
    call: [
      { speaker: "ai", text: "Namaste! I am the AI assistant calling from Sharma Clinic. You missed your visit yesterday, and I can rebook you. Dr Sharma has 11 am or 4 pm tomorrow." },
      { speaker: "cust", text: "Oh yes, sorry about that. 4 pm works." },
      { speaker: "ai", text: "Done, your 4 pm visit is rebooked. I will send a reminder in the morning. Take care!" },
    ],
    approval: {
      quote: "Consultation fee kitna hai? Any discount for senior citizens?",
      sample: "Approve the standard answer: consultation 600 rupees, senior citizens 500 rupees.",
      approveReply: "The consultation fee is 600 rupees, and for senior citizens the doctor keeps it at 500. See you tomorrow at 10:30 am!",
      holdReply: "The doctor will reply to you directly on this, very soon. Your 10:30 am appointment stays confirmed.",
    },
    followup: { card: "3 patients missed visits this week. Send rebooking reminders on WhatsApp tomorrow between 10 am and 7 pm?" },
    callLead: "Missed visit patient",
    queue: [
      { name: "Sunita", note: "asked about a dental cleaning, 6 days ago" },
      { name: "Manoj", note: "asked about timings, 5 days ago" },
      { name: "Asha", note: "asked about a check up, 4 days ago" },
    ],
    revival: [
      { from: "ai", text: "Namaste Sunita! I am the AI assistant at Sharma Clinic. You asked about a dental cleaning last week. Saturday slots are open, shall I book one for you?", time: "10:04 am" },
      { from: "user", text: "Oh yes. Saturday 11 am please.", time: "10:11 am" },
      { from: "ai", text: "Booked! Saturday 11 am. I will send a reminder on Friday evening.", time: "10:11 am" },
    ],
    team: [
      { who: "AI Employee", text: "Good evening team. Please share what you completed today." },
      { who: "Nurse Priya", text: "3 dressings done and stock checked. Photo attached." },
      { who: "Reception Rahul", text: "All of tomorrow's appointments confirmed by call." },
      { who: "AI Employee", text: "Thank you both. Updates and proof noted for the owner's report." },
    ],
    posts: ["Flu season is here. Evening slots open this week, book on WhatsApp.", "Dr Sharma is available on Saturday mornings this month."],
    reportExtra: ["14 chats answered", "6 appointments booked", "2 reminders sent"],
  },
  {
    id: "salon",
    label: "Salon",
    biz: "Meera Salon",
    s1: [
      { from: "user", text: "Hi, any slot for hair spa tomorrow?", time: "9:14 pm" },
      { from: "ai", text: "Hello! I am the AI assistant at Meera Salon. Yes, tomorrow has 11 am and 4:30 pm free for hair spa. Which works for you?", time: "9:14 pm" },
      { from: "user", text: "4:30. And bridal package kitna ka hai?", time: "9:16 pm" },
      { from: "ai", text: "4:30 pm is requested for you. May I have your name? On the bridal package, the owner confirms prices directly. I have sent her your question.", time: "9:16 pm" },
      { from: "user", text: "Pooja.", time: "9:17 pm" },
      { from: "ai", text: "Thank you Pooja, your 4:30 pm hair spa is booked. I will send a reminder tomorrow morning.", time: "9:17 pm" },
    ],
    s1Caption: "A customer messages at night. The AI assistant gives a first reply in about a minute.",
    call: [
      { speaker: "ai", text: "Namaste! I am the AI assistant calling from Meera Salon. You asked us to tell you when a weekend slot opens. Saturday 11 am is free for your hair spa, shall I book it?" },
      { speaker: "cust", text: "Yes, book it." },
      { speaker: "ai", text: "I have you as Ritu on this number, correct?" },
      { speaker: "cust", text: "Yes." },
      { speaker: "ai", text: "Booked, Ritu. Saturday 11 am, and I will send a reminder on Friday evening." },
    ],
    approval: {
      quote: "And bridal package kitna ka hai?",
      sample: "Approve the standard answer: bridal package 12,000 rupees with a trial included.",
      approveReply: "Our bridal package is 12,000 rupees and includes a trial session. Shall I hold a date for you?",
      holdReply: "The owner will message you the bridal package details directly, very soon. Your 4:30 pm hair spa stays booked.",
    },
    followup: { card: "3 bridal enquiries went quiet this week. Follow up on WhatsApp tomorrow between 10 am and 7 pm?" },
    callLead: "Waitlist customer",
    queue: [
      { name: "Kavita", note: "asked about the bridal package, 6 days ago" },
      { name: "Sneha", note: "asked about party makeup, 5 days ago" },
      { name: "Divya", note: "asked about hair spa rates, 4 days ago" },
    ],
    revival: [
      { from: "ai", text: "Namaste Kavita! I am the AI assistant at Meera Salon. You asked about the bridal package last week. Trial sessions are open this week, shall I book you one?", time: "10:06 am" },
      { from: "user", text: "Yes! Thursday evening?", time: "10:14 am" },
      { from: "ai", text: "Booked, Thursday 6 pm for your trial. I will send a reminder that morning.", time: "10:14 am" },
    ],
    team: [
      { who: "AI Employee", text: "Good evening team. Please share what you completed today." },
      { who: "Ritu", text: "6 clients done. Chair 2 is free after 5 pm tomorrow." },
      { who: "Meena", text: "Bridal trial kit restocked, bill uploaded." },
      { who: "AI Employee", text: "Thank you both. Updates and proof noted for the owner's report." },
    ],
    posts: ["Weekend offer: 20 percent off hair spa. Book on WhatsApp.", "Bridal season is here. Trial sessions open this week."],
    reportExtra: ["11 chats answered", "5 bookings made", "2 slots refilled from the waitlist"],
  },
  {
    id: "realestate",
    label: "Real estate",
    biz: "Mehta Properties",
    s1: [
      { from: "user", text: "Saw your listing. 2BHK in Andheri still available?", time: "11:02 pm" },
      { from: "ai", text: "Hello! I am the AI assistant for Mehta Properties. Yes, two matching 2BHK options are available in Andheri. On pricing, the owner confirms directly, and I can already book you a site visit. Would Saturday work?", time: "11:02 pm" },
      { from: "user", text: "Saturday morning. And what is the final price?", time: "11:04 pm" },
      { from: "ai", text: "Saturday 11 am is requested for you. May I have your name? I have sent your price question to the owner and will come back the moment he confirms.", time: "11:04 pm" },
      { from: "user", text: "Imran.", time: "11:05 pm" },
      { from: "ai", text: "Thank you Imran, your Saturday 11 am site visit is confirmed. Our agent will call you that morning.", time: "11:05 pm" },
    ],
    s1Caption: "A buyer messages at night. The AI assistant gives a first reply in about a minute.",
    call: [
      { speaker: "ai", text: "Namaste! I am the AI assistant calling from Mehta Properties about your enquiry last night. The 2BHK options are ready to view, shall I confirm your Saturday 11 am site visit?" },
      { speaker: "cust", text: "Yes. And the price?" },
      { speaker: "ai", text: "The owner confirms pricing directly, and I have sent him your question. You will hear back today. See you Saturday!" },
    ],
    approval: {
      quote: "And what is the final price?",
      sample: "Approve the standard answer: quote 82 lakh for the Andheri 2BHK.",
      approveReply: "The owner has confirmed: the Andheri 2BHK is priced at 82 lakh. I will share the full details before your Saturday visit.",
      holdReply: "The owner will confirm the price with you directly, very soon. Your Saturday 11 am visit stays confirmed.",
    },
    followup: { card: "3 buyer enquiries went quiet this week. Follow up on WhatsApp tomorrow between 10 am and 7 pm?" },
    callLead: "Last night's enquiry",
    queue: [
      { name: "Sameer", note: "asked about the Andheri 2BHK, 6 days ago" },
      { name: "Neha", note: "asked for a site visit, 5 days ago" },
      { name: "Vikram", note: "asked about options in his budget, 4 days ago" },
    ],
    revival: [
      { from: "ai", text: "Namaste Sameer! I am the AI assistant for Mehta Properties. You asked about the Andheri 2BHK last week. New photos are in and weekend visits are open, shall I book you a slot?", time: "10:09 am" },
      { from: "user", text: "Yes, Sunday works.", time: "10:17 am" },
      { from: "ai", text: "Done, Sunday 11 am site visit booked. Our agent will call you that morning.", time: "10:17 am" },
    ],
    team: [
      { who: "AI Employee", text: "Good evening team. Please share what you completed today." },
      { who: "Agent Arif", text: "2 site visits done, keys returned to office." },
      { who: "Agent Pooja", text: "New listing photos uploaded for Andheri." },
      { who: "AI Employee", text: "Thank you both. Updates and proof noted for the owner's report." },
    ],
    posts: ["New 2BHK listings in Andheri West this week. Message us for a site visit.", "Weekend site visits open. Limited slots on Saturday."],
    reportExtra: ["11 buyer chats answered", "3 site visits booked", "4 old enquiries revived"],
  },
  {
    id: "restaurant",
    label: "Restaurant",
    biz: "Bawarchi Kitchen",
    s1: [
      { from: "user", text: "Table for 6 tomorrow at 8?", time: "8:47 pm" },
      { from: "ai", text: "Hello! I am the AI assistant at Bawarchi Kitchen. For tomorrow 8 pm I can request a table for 6 near the window or in the main hall. Which do you prefer?", time: "8:47 pm" },
      { from: "user", text: "Window. Also can you do a birthday cake?", time: "8:49 pm" },
      { from: "ai", text: "Window table for 6 requested for 8 pm. May I have your name? And on the cake, yes, the kitchen takes birthday orders. What size and flavour would you like?", time: "8:49 pm" },
      { from: "user", text: "Arjun. 1 kg chocolate. Price?", time: "8:50 pm" },
      { from: "ai", text: "Thank you Arjun, your table is confirmed for tomorrow 8 pm. I have sent your cake price question to the owner and will come back the moment he confirms.", time: "8:50 pm" },
    ],
    s1Caption: "A customer messages during the dinner rush. The AI assistant gives a first reply in about a minute.",
    call: [
      { speaker: "ai", text: "Namaste! I am the AI assistant calling from Bawarchi Kitchen. You asked us to call you about the party enquiry you sent last night. The hall is free on Sunday evening, would you like me to hold it?" },
      { speaker: "cust", text: "Yes, hold it for 20 people." },
      { speaker: "ai", text: "Done, the hall is held for Sunday evening, 20 people. The owner will confirm the menu and price with you directly." },
    ],
    approval: {
      quote: "1 kg chocolate. Price?",
      sample: "Approve the standard answer: 950 rupees for a 1 kg chocolate cake.",
      approveReply: "The 1 kg chocolate cake is 950 rupees. Shall I add it to your table for tomorrow 8 pm?",
      holdReply: "The owner will confirm the cake price with you directly, very soon. Your table for tomorrow 8 pm stays confirmed.",
    },
    followup: { card: "3 party enquiries went quiet this week. Follow up on WhatsApp tomorrow between 10 am and 7 pm?" },
    callLead: "Party enquiry callback",
    queue: [
      { name: "Rohit", note: "asked about a party booking, 6 days ago" },
      { name: "Farah", note: "asked about the Sunday thali, 5 days ago" },
      { name: "Dev", note: "asked about bulk orders, 4 days ago" },
    ],
    revival: [
      { from: "ai", text: "Namaste Rohit! I am the AI assistant at Bawarchi Kitchen. You asked about a party booking last week. The hall has weekend dates open, shall I check one for you?", time: "10:12 am" },
      { from: "user", text: "Yes, next Saturday.", time: "10:20 am" },
      { from: "ai", text: "Noted, next Saturday is requested for you. The owner will confirm the menu and price directly.", time: "10:20 am" },
    ],
    team: [
      { who: "AI Employee", text: "Good evening team. Please share what you completed today." },
      { who: "Chef Imran", text: "Sunday special menu is ready, list shared." },
      { who: "Manager Asha", text: "14 tables confirmed for tomorrow." },
      { who: "AI Employee", text: "Thank you both. Updates and proof noted for the owner's report." },
    ],
    posts: ["Sunday special thali is back. Book your table on WhatsApp.", "Planning a birthday? The window section seats 12."],
    reportExtra: ["16 chats answered", "7 tables booked", "1 party enquiry held"],
  },
  {
    id: "coaching",
    label: "Coaching",
    biz: "Vidya Classes",
    s1: [
      { from: "user", text: "Class 10 maths batch available?", time: "10:05 pm" },
      { from: "ai", text: "Hello! I am the AI assistant at Vidya Classes. Yes, the Class 10 maths batch has seats, Monday Wednesday Friday at 5 pm. Would you like a free trial class for your child?", time: "10:05 pm" },
      { from: "user", text: "Yes. Fees kitni hai?", time: "10:07 pm" },
      { from: "ai", text: "I can book the trial for this Wednesday 5 pm. May I have your child's name? On fees, the director confirms directly and I have sent your question.", time: "10:07 pm" },
      { from: "user", text: "Aarav.", time: "10:08 pm" },
      { from: "ai", text: "Thank you, Aarav's trial class is booked for Wednesday 5 pm. I will send a reminder that morning.", time: "10:08 pm" },
    ],
    s1Caption: "A parent messages at night. The AI assistant gives a first reply in about a minute.",
    call: [
      { speaker: "ai", text: "Namaste! I am the AI assistant calling from Vidya Classes. You asked for a call about the Class 10 batch. The Wednesday trial still has seats, shall I book Aarav in?" },
      { speaker: "cust", text: "Yes please." },
      { speaker: "ai", text: "Done, Aarav's trial is booked for Wednesday 5 pm. The director will confirm the fees with you directly. Thank you!" },
    ],
    approval: {
      quote: "Fees kitni hai?",
      sample: "Approve the standard answer: 2,500 rupees per month for Class 10 maths.",
      approveReply: "The Class 10 maths batch is 2,500 rupees per month. Aarav's free trial on Wednesday stays booked, so you can decide after that.",
      holdReply: "The director will confirm the fees with you directly, very soon. Aarav's Wednesday trial stays booked.",
    },
    followup: { card: "3 admission enquiries went quiet this week. Follow up on WhatsApp tomorrow between 10 am and 7 pm?" },
    callLead: "Parent who asked for a call",
    queue: [
      { name: "Mrs Gupta", note: "asked about the Class 10 batch, 6 days ago" },
      { name: "Mr Reddy", note: "asked about timings, 5 days ago" },
      { name: "Mrs Khan", note: "asked about a trial class, 4 days ago" },
    ],
    revival: [
      { from: "ai", text: "Namaste! I am the AI assistant at Vidya Classes. You asked about the Class 10 batch last week. Trial classes are open this Wednesday, shall I book one for your child?", time: "10:03 am" },
      { from: "user", text: "Yes please, for Riya.", time: "10:15 am" },
      { from: "ai", text: "Booked, Riya's trial is on Wednesday 5 pm. I will send a reminder that morning.", time: "10:15 am" },
    ],
    team: [
      { who: "AI Employee", text: "Good evening team. Please share what you completed today." },
      { who: "Maths sir", text: "Test papers checked, marks uploaded." },
      { who: "Office desk", text: "4 fee receipts issued, file updated." },
      { who: "AI Employee", text: "Thank you both. Updates and proof noted for the owner's report." },
    ],
    posts: ["Board exam crash course starts Monday. Limited seats.", "Free trial classes this week for Class 10 maths."],
    reportExtra: ["12 parent chats answered", "4 trial classes booked", "2 fee questions passed to you"],
  },
];
